<script setup>
/**
 * タイムチャート作成：上映回の追加・編集ダイアログ（DEV-14983）
 *
 * - 編集：CM・予告・バッファ分数、開始時刻、営業日、スクリーンを変更できる。本編分数は読み取り専用
 * - 追加：ステップ2 の作品・バージョンから選ぶ。本編分数は作品の上映時間
 * - 変更は画面上の下書きに反映するだけで、保存は画面の「保存」で行う
 */
import { ref, computed, watch, nextTick } from "vue";
import { ElMessageBox } from "element-plus";
import { usePlanState } from "../use-plan";
import {
  SNAP_MINUTES,
  TIME_MAX_MINUTES,
  calcShowTimes,
  hasScreenCapability,
  screenCapabilityLabel,
  shortDateLabel,
  toMinutes,
  toTime,
  versionLabel,
} from "../utils";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // 編集する上映回の _key（追加時は空）
  showKey: { type: String, default: "" },
  // 追加時の初期値 { schedMovieId, versionId, bizDate }
  preset: { type: Object, default: () => ({}) },
});
const emit = defineEmits(["update:modelValue"]);

// 分数入力の上限
const MINUTE_MAX = 120;
// 開始時刻の形式（00:00〜30:00）
const TIME_REG = /^([0-2]\d|30):([0-5]\d)$/;
// 開始時刻の候補（5 分刻み。リストにない時刻も入力できる）
const TIME_OPTIONS = (() => {
  const list = [];
  for (let m = 0; m <= TIME_MAX_MINUTES; m += SNAP_MINUTES) list.push(toTime(m));
  return list;
})();

const state = usePlanState();
const {
  canEdit, days, screens, movies, shows, movieMap, screenMap, versionMap, violationsByShow,
  adMinute, trailerMinute,
} = state;

const formRef = ref(null);
const form = ref({});

const isAdd = computed(() => !props.showKey);
const target = computed(() => shows.value.find((s) => s._key === props.showKey) || null);
const formMovie = computed(() => movieMap.value.get(form.value.schedMovieId));
const formScreen = computed(() => screenMap.value.get(form.value.screenId));
const showViolations = computed(() => (props.showKey ? violationsByShow.value.get(props.showKey) || [] : []));

// 終了・占有終了のプレビュー
const preview = computed(() => {
  const t = calcShowTimes(form.value);
  return { end: toTime(t.end), occupyEnd: toTime(t.occupyEnd) };
});

const dialogTitle = computed(() => {
  if (isAdd.value) return "上映回を追加";
  return canEdit ? "上映回を編集" : "上映回の詳細";
});

/**
 * 作品の対象期間内の上映可能期間（runFrom〜runTo）に含まれる日か。期間が返らない場合は制限しない
 */
const inRun = (movie, d) => {
  if (!movie) return true;
  return (!movie.runFrom || d >= movie.runFrom) && (!movie.runTo || d <= movie.runTo);
};

/**
 * 上映期間外なら期間内の最初の日に寄せる
 */
const fitDay = (movie, d) => {
  if (inRun(movie, d)) return d;
  return days.value.find((day) => inRun(movie, day)) || d;
};

const versionText = computed(() => {
  const v = versionMap.value.get(`${form.value.schedMovieId}|${form.value.versionId ?? ""}`);
  return v ? versionLabel(v) : "削除されたバージョン";
});

/**
 * ダイアログを開いたときにフォームを初期化
 */
const initForm = () => {
  if (target.value) {
    form.value = { ...target.value };
  } else {
    const movie = movieMap.value.get(props.preset.schedMovieId);
    form.value = {
      schedMovieId: props.preset.schedMovieId ?? null,
      versionId: props.preset.versionId ?? (movie?.versions?.length === 1 ? movie.versions[0].versionId : null),
      bizDate: fitDay(movie, props.preset.bizDate || days.value[0]),
      screenId: props.preset.screenId ?? null,
      startTime: "",
      adMinute: adMinute.value ?? 0,
      trailerMinute: trailerMinute.value ?? 0,
      bufferMinute: 0,
      featureMinute: movie?.durationMinute ?? 0,
    };
  }
  nextTick(() => formRef.value?.clearValidate());
};

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) initForm();
  },
);

const onMovieChange = () => {
  const movie = formMovie.value;
  form.value.versionId = movie?.versions?.length === 1 ? movie.versions[0].versionId : null;
  form.value.featureMinute = movie?.durationMinute ?? 0;
  form.value.bizDate = fitDay(movie, form.value.bizDate);
};

const formRules = {
  schedMovieId: [{ required: true, message: "作品を選択してください", trigger: "change" }],
  versionId: [{ required: true, message: "バージョンを選択してください", trigger: "change" }],
  bizDate: [{ required: true, message: "営業日を選択してください", trigger: "change" }],
  screenId: [{ required: true, message: "スクリーンを選択してください", trigger: "change" }],
  startTime: [{
    validator: (_rule, value, callback) => {
      if (!value) return callback(new Error("開始時刻を入力してください"));
      if (!TIME_REG.test(value) || toMinutes(value) > TIME_MAX_MINUTES) {
        return callback(new Error("開始時刻は 00:00〜30:00 の HH:mm 形式で入力してください"));
      }
      return callback();
    },
    trigger: "change",
  }],
};

const close = () => emit("update:modelValue", false);

const submit = async () => {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }
  const patch = {
    screenId: form.value.screenId,
    bizDate: form.value.bizDate,
    startTime: form.value.startTime,
    adMinute: form.value.adMinute ?? 0,
    trailerMinute: form.value.trailerMinute ?? 0,
    bufferMinute: form.value.bufferMinute ?? 0,
  };
  if (isAdd.value) {
    state.addShow({
      ...patch,
      schedMovieId: form.value.schedMovieId,
      versionId: form.value.versionId,
      featureMinute: form.value.featureMinute,
    });
  } else {
    state.updateShow(props.showKey, patch);
  }
  close();
};

const handleDelete = async () => {
  try {
    await ElMessageBox.confirm("この上映回を削除しますか？（「保存」するまで確定しません）", "確認", {
      confirmButtonText: "削除",
      cancelButtonText: "キャンセル",
      type: "warning",
    });
  } catch {
    return;
  }
  state.removeShow(props.showKey);
  close();
};
</script>

<template>
  <el-dialog
    :model-value="modelValue"
    :title="dialogTitle"
    width="560px"
    append-to-body
    :close-on-click-modal="false"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <el-alert
      v-for="v in showViolations"
      :key="v._key"
      type="error"
      :closable="false"
      show-icon
      :title="`${v.typeLabel}：${v.message || ''}`"
      class="violation-alert"
    />

    <el-form
      ref="formRef"
      :model="form"
      :rules="formRules"
      :disabled="!canEdit"
      label-width="110px"
      @submit.prevent
    >
      <el-form-item label="作品" prop="schedMovieId">
        <el-select v-if="isAdd" v-model="form.schedMovieId" filterable placeholder="作品を選択" @change="onMovieChange">
          <el-option v-for="m in movies" :key="m.schedMovieId" :label="m.title" :value="m.schedMovieId" />
        </el-select>
        <span v-else class="strong">{{ formMovie?.title || "削除された作品" }}</span>
      </el-form-item>
      <el-form-item label="バージョン" prop="versionId">
        <el-select v-if="isAdd" v-model="form.versionId" :disabled="!formMovie" placeholder="バージョンを選択">
          <el-option
            v-for="v in formMovie?.versions || []"
            :key="v.versionId"
            :label="versionLabel(v)"
            :value="v.versionId"
          />
        </el-select>
        <span v-else>{{ versionText }}</span>
      </el-form-item>
      <el-form-item label="営業日" prop="bizDate">
        <el-select v-model="form.bizDate">
          <el-option
            v-for="d in days"
            :key="d"
            :label="inRun(formMovie, d) ? shortDateLabel(d) : `${shortDateLabel(d)}（上映期間外）`"
            :value="d"
            :disabled="!inRun(formMovie, d)"
          />
        </el-select>
        <p v-if="formMovie?.runFrom || formMovie?.runTo" class="hint">
          期間内の上映可能期間：{{ shortDateLabel(formMovie.runFrom || days[0]) }}〜{{ shortDateLabel(formMovie.runTo || days[days.length - 1]) }}
        </p>
      </el-form-item>
      <el-form-item label="スクリーン" prop="screenId">
        <el-select v-model="form.screenId" placeholder="スクリーンを選択">
          <el-option
            v-for="s in screens"
            :key="s.screenId"
            :label="`${s.name}（${s.maxSeats}席）`"
            :value="s.screenId"
          />
        </el-select>
        <p v-if="formScreen" class="hint">
          {{ formScreen.openTime }}–{{ formScreen.closeTime }}
          <template v-if="hasScreenCapability(formScreen)">／ {{ screenCapabilityLabel(formScreen) }}</template>
        </p>
      </el-form-item>
      <el-form-item label="開始時刻" prop="startTime">
        <el-select
          v-model="form.startTime"
          filterable
          allow-create
          default-first-option
          placeholder="例 10:00 / 25:30"
          class="time-select"
        >
          <el-option v-for="t in TIME_OPTIONS" :key="t" :label="t" :value="t" />
        </el-select>
        <p class="hint">CM開始の時刻。24:00 以降は 25:30 のように入力（最大 30:00）</p>
      </el-form-item>
      <div class="minute-grid">
        <el-form-item label="CM（分）">
          <el-input-number v-model="form.adMinute" :min="0" :max="MINUTE_MAX" :step="1" step-strictly controls-position="right" />
        </el-form-item>
        <el-form-item label="予告（分）">
          <el-input-number v-model="form.trailerMinute" :min="0" :max="MINUTE_MAX" :step="1" step-strictly controls-position="right" />
        </el-form-item>
        <el-form-item label="本編（分）">
          <span class="strong">{{ form.featureMinute || "-" }}</span>
          <span class="muted">（変更不可）</span>
        </el-form-item>
        <el-form-item label="バッファ（分）">
          <el-input-number v-model="form.bufferMinute" :min="0" :max="MINUTE_MAX" :step="1" step-strictly controls-position="right" />
        </el-form-item>
      </div>
      <el-form-item label="終了 / 占有終了">
        <span class="time-preview">{{ preview.end || "-" }} / {{ preview.occupyEnd || "-" }}</span>
      </el-form-item>
      <el-form-item v-if="!isAdd" label="予測動員">
        <span v-if="target?.estSeats != null">{{ target.estSeats }}人</span>
        <span v-else class="muted">算出不可（データ不足）</span>
        <el-tag size="small" effect="plain" class="source-tag">{{ target?.source === "AI" ? "AI案" : "手動" }}</el-tag>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="footer">
        <el-button
          v-if="canEdit && !isAdd"
          v-hasPermi="['dp:scheduleAi:plan:edit']"
          type="danger"
          plain
          @click="handleDelete"
        >
          削除
        </el-button>
        <span class="footer__spacer" />
        <el-button @click="close">{{ canEdit ? "キャンセル" : "閉じる" }}</el-button>
        <el-button v-if="canEdit" v-hasPermi="['dp:scheduleAi:plan:edit']" type="primary" @click="submit">OK</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="less" scoped>
.violation-alert {
  margin-bottom: 8px;
}

.strong {
  font-weight: 600;
}

.muted {
  margin-left: 4px;
  font-size: 12px;
  color: var(--text-tertiary, #757575);
}

.hint {
  width: 100%;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-tertiary, #757575);
}

.time-select {
  width: 180px;
}

.minute-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  .el-input-number {
    width: 120px;
  }
}

.time-preview {
  font-variant-numeric: tabular-nums;
}

.source-tag {
  margin-left: 8px;
}

.footer {
  display: flex;
  align-items: center;

  &__spacer {
    flex: 1;
  }
}

@media (max-width: 600px) {
  .minute-grid {
    grid-template-columns: 1fr;
  }
}
</style>
